import { useState, useEffect } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  AreaChart, Area, PieChart, Pie, Cell,
} from "recharts";
import { T, SEV_COLOR } from "../utils/theme";
import { api, ENDPOINTS, formatCurrency } from "../utils/api";
import { KpiCard, Card, Spinner, StatusBadge, SeverityBadge, CvssScore, ProgressBar } from "../components/UI";
import {
  Users, Building2, Shield, Bug,
  CheckCircle, Clock, XCircle, AlertTriangle, Eye,
} from "lucide-react";

/*
  CORRECTIONS vs doc API :

  1. GET /hackers/profil/ranking (Public) → pas de token requis.
     L'appel était `api(ENDPOINTS.publicRanking)` sans options ni token → correct,
     mais api() envoie Content-Type JSON sans body, ce qui peut causer des erreurs.
     CORRECTION : fetch direct pour les endpoints publics.

  2. GET /reports/by-company (Auth + Entreprise) → avec token admin.
     L'admin peut accéder aux rapports via cet endpoint avec son token.

  3. Normalisation de la réponse : data.data || data || []
     La doc retourne { success, data: [...] } pour la plupart des endpoints.

  4. adminStats → GET /admin/stats (Auth + Admin)
*/

const BASE_URL = import.meta.env.VITE_API_URL || "https://api.200bounty.com";

const BOUNTY_DATA = [
  { m: "Jan", bounties: 4200,  rapports: 28 },
  { m: "Fév", bounties: 6800,  rapports: 41 },
  { m: "Mar", bounties: 5100,  rapports: 35 },
  { m: "Avr", bounties: 9200,  rapports: 58 },
  { m: "Mai", bounties: 7400,  rapports: 46 },
  { m: "Jun", bounties: 11000, rapports: 72 },
  { m: "Jul", bounties: 8900,  rapports: 61 },
  { m: "Aoû", bounties: 14200, rapports: 89 },
];

const SEV_PIE = [
  { name: "Critique", value: 8,  color: SEV_COLOR?.critique || "#ef4444" },
  { name: "Haute",    value: 22, color: SEV_COLOR?.haute    || "#f97316" },
  { name: "Moyenne",  value: 35, color: SEV_COLOR?.moyenne  || "#eab308" },
  { name: "Faible",   value: 25, color: SEV_COLOR?.faible   || "#22c55e" },
  { name: "Info",     value: 10, color: SEV_COLOR?.info     || "#6b7280" },
];

const ACTIVITY = [
  { h: "00h", events: 3  }, { h: "03h", events: 1  }, { h: "06h", events: 5  },
  { h: "09h", events: 18 }, { h: "12h", events: 24 }, { h: "15h", events: 31 },
  { h: "18h", events: 20 }, { h: "21h", events: 12 },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, padding: "10px 16px", fontSize: 12 }}>
      <p style={{ color: T.muted, marginBottom: 4 }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color, fontWeight: 700 }}>
          {p.name}: {typeof p.value === "number" && p.value > 500 ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

// Helper : normalise la réponse API { success, data } || tableau direct
const normalize = (json) => {
  if (!json) return null;
  if (Array.isArray(json)) return json;
  if (json.data !== undefined) return json.data;
  return json;
};

const DashboardPage = ({ token, user }) => {
  const [stats, setStats]               = useState(null);
  const [ranking, setRanking]           = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        /*
          CORRECTION 1 : GET /admin/stats (Auth + Admin) avec token
        */
        const statsRes = await api(ENDPOINTS.adminStats, { method: "GET" }, token);
        setStats(normalize(statsRes));

        /*
          CORRECTION 2 : GET /hackers/profil/ranking (Public) — fetch direct sans token
          L'endpoint est Public, pas besoin d'Authorization header.
        */
        const rankRes = await fetch(`${BASE_URL}${ENDPOINTS.publicRanking}`)
          .then(r => r.json())
          .catch(() => ({ data: [] }));
        const rankData = normalize(rankRes) || [];
        setRanking(Array.isArray(rankData) ? rankData.slice(0, 6) : []);

        /*
          CORRECTION 3 : GET /reports/by-company (Auth + Entreprise/Admin) avec token
        */
        const repsRes = await api(ENDPOINTS.reportsByCompany, { method: "GET" }, token)
          .catch(() => ({ data: [] }));
        const repsData = normalize(repsRes) || [];
        setRecentReports(Array.isArray(repsData) ? repsData.slice(0, 6) : []);

      } catch {
        setStats({});
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  if (loading) return <Spinner />;

  const kpis = [
    { label: "Hackers Inscrits",  value: stats?.totalHackers   ?? "—", icon: Users,     color: T.blue,   trend: "+12%", trendUp: true, sub: "vs mois dernier", delay: 0   },
    { label: "Entreprises",       value: stats?.totalCompanies ?? "—", icon: Building2, color: T.purple, trend: "+3",   trendUp: true, sub: "nouvelles",       delay: 60  },
    { label: "Programmes Actifs", value: stats?.totalPrograms  ?? "—", icon: Shield,    color: T.cyan,   trend: "+5",   trendUp: true, sub: "en cours",        delay: 120 },
    { label: "Rapports Totaux",   value: stats?.totalReports   ?? "—", icon: Bug,       color: T.orange, trend: "+28",  trendUp: true, sub: "ce mois",         delay: 180 },
  ];

  const statusCounts = {
    validé:     recentReports.filter(r => r.statut === "validé").length,
    en_attente: recentReports.filter(r => r.statut === "en_attente").length,
    rejeté:     recentReports.filter(r => r.statut === "rejeté").length,
    duplicate:  recentReports.filter(r => r.statut === "duplicate").length,
  };

  return (
    <div className="fade-up" style={{ padding: "24px 28px", overflowY: "auto", flex: 1 }}>
      {/* Welcome */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontFamily: T.display, fontSize: 22, fontWeight: 900, color: T.text }}>
          Bienvenue, {user?.nom || user?.prenom || "Admin"} 👋
        </div>
        <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
          Vue d'ensemble de la plateforme 200Bounty
        </div>
      </div>

      {/* KPIs */}
      <div className="grid-4" style={{ marginBottom: 22 }}>
        {kpis.map((k, i) => <KpiCard key={i} {...k} />)}
      </div>

      {/* Charts Row 1 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 16, marginBottom: 20 }}>
        {/* Bounties + Reports (données statiques de démonstration) */}
        <Card style={{ padding: 22 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
            <div>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 15 }}>Bounties & Rapports</div>
              <div style={{ fontSize: 11, color: T.muted }}>8 derniers mois</div>
            </div>
            <div style={{ display: "flex", gap: 16, fontSize: 11 }}>
              <span style={{ color: T.blue }}>● Bounties</span>
              <span style={{ color: T.green }}>● Rapports</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={BOUNTY_DATA} barGap={4}>
              <XAxis
                dataKey="m"
                tick={{ fill: T.muted, fontSize: 11, fontFamily: T.mono }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: `${T.blue}08` }} />
              <Bar dataKey="bounties" name="Bounties (€)" fill={T.blue} radius={[5,5,0,0]} background={{ fill: T.surface, radius: [5,5,0,0] }} />
              <Bar dataKey="rapports" name="Rapports"     fill={T.green} radius={[5,5,0,0]} background={{ fill: T.surface, radius: [5,5,0,0] }} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Severity Pie */}
        <Card style={{ padding: 22 }}>
          <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Sévérité des rapports</div>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 16 }}>Distribution globale</div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie
                  data={SEV_PIE}
                  cx="50%" cy="50%"
                  innerRadius={42} outerRadius={62}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {SEV_PIE.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            <div style={{ flex: 1 }}>
              {SEV_PIE.map((s, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 8, height: 8, borderRadius: "50%", background: s.color, flexShrink: 0 }} />
                    <span style={{ fontSize: 11, color: T.textSub }}>{s.name}</span>
                  </div>
                  <span style={{ fontSize: 11, fontWeight: 700, color: s.color }}>{s.value}%</span>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div style={{ display: "grid", gridTemplateColumns: "340px 1fr", gap: 16, marginBottom: 20 }}>
        {/* Activity */}
        <Card style={{ padding: 22 }}>
          <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Activité 24h</div>
          <div style={{ fontSize: 11, color: T.muted, marginBottom: 16 }}>Évènements par heure</div>
          <ResponsiveContainer width="100%" height={140}>
            <AreaChart data={ACTIVITY}>
              <defs>
                <linearGradient id="actGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor={T.cyan} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={T.cyan} stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="h" tick={{ fill: T.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
              <YAxis hide />
              <Tooltip content={<CustomTooltip />} />
              <Area type="monotone" dataKey="events" name="Évènements" stroke={T.cyan} fill="url(#actGrad)" strokeWidth={2} dot={false} />
            </AreaChart>
          </ResponsiveContainer>

          {/* Status summary — données réelles depuis l'API */}
          <div style={{ borderTop: `1px solid ${T.border}`, marginTop: 14, paddingTop: 14, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            {[
              { icon: CheckCircle,  label: "Validés",    count: statusCounts.validé,     color: T.green  },
              { icon: Clock,        label: "En attente", count: statusCounts.en_attente, color: T.yellow },
              { icon: XCircle,      label: "Rejetés",    count: statusCounts.rejeté,     color: T.red    },
              { icon: AlertTriangle,label: "Duplicates", count: statusCounts.duplicate,  color: T.purple },
            ].map((s, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                <s.icon size={13} color={s.color} />
                <span style={{ fontSize: 11, color: T.muted }}>{s.label}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: s.color, marginLeft: "auto" }}>{s.count}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Top Hackers — depuis GET /hackers/profil/ranking (Public) */}
        <Card>
          <div style={{
            padding: "16px 20px", borderBottom: `1px solid ${T.border}`,
            display: "flex", justifyContent: "space-between", alignItems: "center",
          }}>
            <div>
              <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 15 }}>🏆 Top Hackers</div>
              <div style={{ fontSize: 11, color: T.muted }}>Classement par points</div>
            </div>
          </div>
          {ranking.length === 0 ? (
            <div style={{ padding: 20, fontSize: 12, color: T.muted }}>Aucune donnée</div>
          ) : (
            ranking.map((h, i) => {
              const maxPts = ranking[0]?.points || 1;
              return (
                <div key={i} className="hov-row" style={{
                  display: "flex", alignItems: "center", gap: 12, padding: "11px 20px",
                  borderBottom: `1px solid ${T.border}22`,
                }}>
                  <div style={{ width: 24, fontSize: 15, textAlign: "center", flexShrink: 0 }}>
                    {["🥇","🥈","🥉","4️⃣","5️⃣","6️⃣"][i]}
                  </div>
                  <div style={{
                    width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
                    background: `hsl(${i * 55 + 190}, 55%, 38%)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 12, fontWeight: 800, color: "#fff",
                  }}>
                    {(h.pseudo || "?")[0].toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: T.text }}>{h.pseudo}</div>
                    <ProgressBar value={h.points || 0} max={maxPts} color={`hsl(${i * 55 + 190}, 65%, 55%)`} height={3} />
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontFamily: T.display, fontWeight: 800, fontSize: 15, color: i === 0 ? T.yellow : T.text }}>
                      {(h.points || 0).toLocaleString()}
                    </div>
                    <div style={{ fontSize: 9, color: T.muted }}>pts</div>
                  </div>
                </div>
              );
            })
          )}
        </Card>
      </div>

      {/* Recent Reports — depuis GET /reports/by-company (Auth) */}
      <Card>
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${T.border}`,
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}>
          <div>
            <div style={{ fontFamily: T.display, fontWeight: 700, fontSize: 15 }}>Derniers Rapports</div>
            <div style={{ fontSize: 11, color: T.muted }}>Activité récente</div>
          </div>
          <button className="btn btn-ghost btn-sm"><Eye size={12} /> Voir tout</button>
        </div>
        {recentReports.length === 0 ? (
          <div style={{ padding: "20px 20px", fontSize: 12, color: T.muted }}>Aucun rapport récent</div>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th><th>TITRE</th><th>HACKER</th><th>SÉVÉRITÉ</th><th>STATUT</th><th>CVSS</th><th>DATE</th>
              </tr>
            </thead>
            <tbody>
              {recentReports.map((r, i) => (
                <tr key={r.id || i}>
                  <td style={{ color: T.blue, fontWeight: 700, fontSize: 11 }}>
                    {r.id?.slice(0, 8) || `RPT-${i + 1}`}
                  </td>
                  <td style={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {r.titre || "—"}
                  </td>
                  <td style={{ color: T.textSub }}>
                    {r.hacker?.pseudo || r.hackerId?.slice(0, 8) || "—"}
                  </td>
                  <td><SeverityBadge severity={r.severite} /></td>
                  <td><StatusBadge status={r.statut || "en_attente"} /></td>
                  <td><CvssScore score={r.cvssScore || r.cvssScoreFinal} /></td>
                  <td style={{ color: T.muted, fontSize: 11 }}>
                    {r.createdAt ? new Date(r.createdAt).toLocaleDateString("fr") : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
};

export default DashboardPage;